import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';

const NavigationHeader = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Helper function to detect user context from current location
  const getUserContext = () => {
    const isOnMePage = location.pathname.startsWith('/me') ||
                      location.pathname.startsWith('/playerPage') ||
                      location.pathname === '/player-profiles';
    const isOnTournamentPage = location.pathname.startsWith('/tournaments') ||
                              location.pathname === '/organizer' ||
                              location.pathname === '/newEvent';

    if (isOnMePage) {
      return 'creator';
    }
    if (isOnTournamentPage) {
      return 'tournament';
    }
    // Check dashboard access - default based on user role
    return 'tournament'; // Default to tournament context
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Preserve context when logging out
      const context = getUserContext();
      if (context === 'creator') {
        navigate('/me');
      } else {
        navigate('/tournaments');
      }
    } catch (error) {
    }
  };

  const handleSignIn = () => {
    navigate('/auth?mode=login');
  };

  const handleSignUp = () => {
    navigate('/auth?mode=signup');
  };

  const handleLogo = () => {
    if (currentUser) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  // Simplified tabs - only show Admin for admin users
  const tabs = [
    ...(userProfile?.admin ? [{ name: 'Admin', path: '/admin' }] : [])
  ];

  const isActiveTab = (path) => {
    // Special handling for smart routes
    if (path === '/tournaments') {
      return location.pathname === '/tournaments' || location.pathname === '/organizer';
    }
    if (path === '/me') {
      return location.pathname === '/me' || location.pathname === '/player-profiles';
    }
    return location.pathname === path;
  };

  return (
    <nav className="" style={{ 
      // background: 'linear-gradient(-135deg, #749555 50%, #3066be 100%)',
      background: '#749555',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    }} >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:justify-between">
          {/* Logo/Brand - centered on mobile, left on desktop */}
          <div className="flex items-center sm:justify-start sm:flex-initial">
            <button
              onClick={handleLogo}
              className="text-xl font-bold text-black  transition-colors flex items-center gap-1"
            >
              <img 
                src="/ChessWeb.site_Transparent.png" 
                alt="ChessWeb.Site Logo" 
                className="w-10 h-10"
              />
              ChessWeb.site
            </button>
          </div>

          {/* Main Navigation - always visible on desktop, conditional on mobile */}
          <div className="flex items-center space-x-8">
            {/* Register Button - Always visible */}
            {/* <button
              onClick={handleRegister}
              className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors shadow-sm"
            >
              Register
            </button> */}

            {currentUser ? (
              <>
                {/* Desktop Navigation - Hidden on mobile */}
                <div className="nav-desktop-buttons space-x-4 flex items-center">
                  {/* Admin link - only for admin users */}
                  {tabs.map((tab) => (
                    <button
                      key={tab.name}
                      onClick={() => navigate(tab.path)}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActiveTab(tab.path)
                          ? 'bg-chess-cream text-black'
                          : 'text-black hover:text-chess-cream hover:bg-chess-green/20'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}

                  {/* Avatar Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                      className="flex items-center gap-2 p-1 rounded-full hover:bg-chess-green/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-chess-cream flex items-center justify-center">
                        {userProfile?.avatar ? (
                          <img src={userProfile.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <i className="fas fa-user text-chess-green text-sm"></i>
                        )}
                      </div>
                      <svg
                        className={`w-4 h-4 text-black transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Avatar Dropdown Menu */}
                    {isMobileMenuOpen && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsMobileMenuOpen(false)}
                        />

                        {/* Dropdown */}
                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50 overflow-hidden border border-gray-200">
                          <button
                            onClick={() => {
                              navigate('/dashboard');
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-50 text-black"
                          >
                            <i className="fas fa-home mr-2 text-gray-500"></i>
                            Dashboard
                          </button>
                          <button
                            onClick={() => {
                              navigate('/account');
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-50 text-black"
                          >
                            <i className="fas fa-gear mr-2 text-gray-500"></i>
                            Settings
                          </button>
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 text-red-500 font-medium transition-colors hover:bg-gray-50 border-t border-gray-200"
                          >
                            <i className="fas fa-sign-out-alt mr-2"></i>
                            Sign Out
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Mobile Navigation - Avatar dropdown */}
                <div className="tab-nav-mobile items-center">
                  <div className="relative">
                    <button
                      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                      className="flex items-center gap-2 p-1 rounded-full hover:bg-chess-green/20 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-chess-cream flex items-center justify-center">
                        {userProfile?.avatar ? (
                          <img src={userProfile.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <i className="fas fa-user text-chess-green text-sm"></i>
                        )}
                      </div>
                    </button>

                    {/* Mobile Dropdown Menu */}
                    {isMobileMenuOpen && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-40 bg-black bg-opacity-25"
                          onClick={() => setIsMobileMenuOpen(false)}
                        />

                        {/* Dropdown */}
                        <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50 overflow-hidden border border-gray-200">
                          <button
                            onClick={() => {
                              navigate('/dashboard');
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-50 text-black"
                          >
                            <i className="fas fa-home mr-2 text-gray-500"></i>
                            Dashboard
                          </button>
                          {tabs.map((tab) => (
                            <button
                              key={tab.name}
                              onClick={() => {
                                navigate(tab.path);
                                setIsMobileMenuOpen(false);
                              }}
                              className={`w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-50 ${
                                isActiveTab(tab.path) ? 'bg-gray-100 text-black' : 'text-black'
                              }`}
                            >
                              {tab.name}
                            </button>
                          ))}
                          <button
                            onClick={() => {
                              navigate('/account');
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 font-medium transition-colors hover:bg-gray-50 text-black"
                          >
                            <i className="fas fa-gear mr-2 text-gray-500"></i>
                            Settings
                          </button>
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 text-red-500 font-medium transition-colors hover:bg-gray-50 border-t border-gray-200"
                          >
                            <i className="fas fa-sign-out-alt mr-2"></i>
                            Sign Out
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Unauthenticated Navigation - desktop only */}
                <div className="nav-desktop-buttons flex items-center space-x-3">
                  <button
                    onClick={handleSignIn}
                    className="text-black hover:text-chess-cream px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Log In
                  </button>
                  <button
                    onClick={handleSignUp}
                    className="bg-chess-cream text-gray-900 px-4 py-2 rounded-full text-sm font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Get Started
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavigationHeader; 